import type { CatalogItem, Category, Source } from '@viva/core';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ImageLoaderContext, LazyImage } from '@viva/ui';
import { Icon } from '../components/Icon';
import { imageUrl } from '../network';
import type { ReactNode } from 'react';
import { PosterCard } from '../components/ItemViews';
import { t } from '../i18n';
import { useCatalog } from '../state/catalog-context';
import { usePlay } from '../state/playback-context';
import type { WatchEntry } from '../watch-progress';
import { useWatching } from '../watch-progress';

/** Category rows under "Continue watching" and "Recently added". */
const CATEGORY_ROWS = 10;
const ROW_ITEMS = 24;
/** Titles in the big slider at the top; until there are real suggestions, the newest ones. */
const HERO_ITEMS = 8;
const HERO_SLIDE_MS = 7000;
/** Finger travel, in pixels, that counts as a swipe to the next or previous title. */
const HERO_SWIPE_PX = 40;

interface FrontPageProps {
  source: Source;
  kind: 'movie' | 'series';
  /** Newest first, already narrowed by the language/country filter. */
  recent: CatalogItem[];
  /** Categories left by the filter, in provider order. */
  categories: Category[];
  onSeeRecent: () => void;
  onSeeCategory: (key: string) => void;
}

/** Movies and Series front page: rows to scroll sideways, like a streaming service's start page. */
export function FrontPage({ source, kind, recent, categories, onSeeRecent, onSeeCategory }: FrontPageProps) {
  const watching = useWatching(source.id, kind);
  const play = usePlay();
  const rows = categories.filter((category) => category.itemCount > 0).slice(0, CATEGORY_ROWS);

  return (
    <div className="front">
      {recent.length > 0 ? <HeroSlider items={recent.slice(0, HERO_ITEMS)} onOpen={(item) => play({ item })} /> : null}
      {watching.length > 0 ? (
        <Shelf title={t('browse.continue')}>
          {watching.map((entry) => (
            <ContinueCard key={`${entry.sourceId}:${entry.series?.key ?? entry.item.key}`} entry={entry} onOpen={() => play(entry.series ? { item: entry.series } : { item: entry.item })} />
          ))}
        </Shelf>
      ) : null}
      {recent.length > 0 ? (
        <Shelf title={t('browse.recent')} onSeeAll={onSeeRecent}>
          {recent.slice(0, ROW_ITEMS).map((item) => (
            <div key={item.key} className="shelf__card">
              <PosterCard item={item} onOpen={(opened) => play({ item: opened })} />
            </div>
          ))}
        </Shelf>
      ) : null}
      {rows.map((category) => (
        <CategoryShelf key={category.key} source={source} kind={kind} category={category} onSeeAll={() => onSeeCategory(category.key)} />
      ))}
    </div>
  );
}

function ContinueCard({ entry, onOpen }: { entry: WatchEntry; onOpen: () => void }) {
  const { season, episode } = entry.item.data;
  const label = entry.series && season !== undefined && episode !== undefined ? `S${season} E${episode}` : undefined;
  return (
    <div className="shelf__card">
      <PosterCard
        item={entry.series ?? entry.item}
        onOpen={onOpen}
        caption={label}
        progress={entry.duration > 0 ? entry.position / entry.duration : undefined}
      />
    </div>
  );
}

function CategoryShelf({ source, kind, category, onSeeAll }: { source: Source; kind: 'movie' | 'series'; category: Category; onSeeAll: () => void }) {
  const { catalog } = useCatalog();
  const play = usePlay();
  const [items, setItems] = useState<CatalogItem[] | null>(null);
  useEffect(() => {
    let active = true;
    catalog.listItems({ sourceId: source.id, kind, categoryKey: category.key, offset: 0, limit: ROW_ITEMS }).then(
      (page) => active && setItems(page.items),
      () => active && setItems([]),
    );
    return () => {
      active = false;
    };
  }, [catalog, source.id, kind, category.key]);
  if (items !== null && items.length === 0) return null;
  return (
    <Shelf title={category.name || t('browse.other')} onSeeAll={onSeeAll}>
      {(items ?? Array.from({ length: 6 }, () => undefined)).map((item, index) => (
        <div key={item?.key ?? index} className="shelf__card">
          <PosterCard item={item} onOpen={(opened) => play({ item: opened })} />
        </div>
      ))}
    </Shelf>
  );
}

function Shelf({ title, onSeeAll, children }: { title: string; onSeeAll?: () => void; children: ReactNode }) {
  const track = useRef<HTMLDivElement>(null);
  // Which ends the row can still glide to; an arrow shows only where there is more.
  const [room, setRoom] = useState({ back: false, forward: false });
  const measure = useCallback(() => {
    const element = track.current;
    if (!element) return;
    const back = element.scrollLeft > 4;
    const forward = element.scrollLeft + element.clientWidth < element.scrollWidth - 4;
    setRoom((old) => (old.back === back && old.forward === forward ? old : { back, forward }));
  }, []);
  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  });
  // Arrows glide the row a screen's width. Touch swipes it, and the remote's ◀ ▶ move the highlight (which scrolls it).
  const page = (direction: 1 | -1) => {
    const element = track.current;
    if (!element) return;
    const by = direction * Math.max(200, element.clientWidth * 0.85);
    // Chromium 68 has no smooth scrollTo options: it jumps instead.
    if (typeof element.scrollBy === 'function') element.scrollBy({ left: by, behavior: 'smooth' });
    else element.scrollLeft += by;
  };
  return (
    <section className="shelf">
      <header className="shelf__header">
        <h2 className="shelf__title">{title}</h2>
        {onSeeAll ? (
          <button type="button" className="shelf__more" onClick={onSeeAll}>
            {t('browse.seeAll')}
          </button>
        ) : null}
      </header>
      <div className="shelf__body">
        {room.back ? (
          <button type="button" className="shelf__arrow shelf__arrow--prev" tabIndex={-1} aria-label={t('browse.previous')} onClick={() => page(-1)}>
            <Icon name="back" />
          </button>
        ) : null}
        <div className="shelf__track" ref={track} onScroll={measure} data-nav-row>
          {children}
        </div>
        {room.forward ? (
          <button type="button" className="shelf__arrow shelf__arrow--next" tabIndex={-1} aria-label={t('browse.next')} onClick={() => page(1)}>
            <Icon name="next" />
          </button>
        ) : null}
      </div>
    </section>
  );
}

/** Big slides at the top: blurred poster behind, the poster, title and Play. Changes by itself until pointed at. */
function HeroSlider({ items, onOpen }: { items: CatalogItem[]; onOpen: (item: CatalogItem) => void }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = items.length;
  const current = count > 0 ? index % count : 0;
  const go = useCallback((step: number) => setIndex((value) => (value + step + count) % count), [count]);
  const loader = useContext(ImageLoaderContext);
  const touch = useRef<{ x: number; y: number; sideways: boolean | null } | null>(null);
  const [drag, setDrag] = useState(0);
  // Fetch every slide's poster up front, so a swipe shows the next one at once instead of waiting for it to load.
  const posters = items.map((entry) => entry.logo ?? '').join('\n');
  useEffect(() => {
    const cancels = posters.split('\n').map((logo) => imageUrl(logo || null)).filter((src): src is string => !!src).map((src) => loader.load(src, () => undefined));
    return () => cancels.forEach((cancel) => cancel());
  }, [posters, loader]);
  useEffect(() => {
    const still = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (paused || count < 2 || still) return undefined;
    const timer = setTimeout(() => go(1), HERO_SLIDE_MS);
    return () => clearTimeout(timer);
  }, [current, paused, count, go]);
  const item = items[current];
  if (!item) return null;
  const facts = [item.data.year, item.data.rating ? `★ ${item.data.rating}` : null].filter(Boolean).join(' · ');
  return (
    <div
      className="hero"
      role="region"
      aria-roledescription="carousel"
      aria-label={t('browse.recent')}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onTouchStart={(event) => {
        const point = event.touches[0];
        touch.current = point ? { x: point.clientX, y: point.clientY, sideways: null } : null;
        setPaused(true);
      }}
      onTouchMove={(event) => {
        const start = touch.current;
        const point = event.touches[0];
        if (!start || !point || count < 2) return;
        const dx = point.clientX - start.x;
        const dy = point.clientY - start.y;
        // Decide once per touch whether it is a sideways swipe or a page scroll.
        if (start.sideways === null && Math.abs(dx) + Math.abs(dy) > 8) start.sideways = Math.abs(dx) > Math.abs(dy);
        if (start.sideways) setDrag(dx);
      }}
      onTouchEnd={(event) => {
        const start = touch.current;
        const end = event.changedTouches[0]?.clientX;
        if (start?.sideways && end !== undefined && Math.abs(end - start.x) > HERO_SWIPE_PX) go(end < start.x ? 1 : -1);
        touch.current = null;
        setDrag(0);
        setPaused(false);
      }}
      onTouchCancel={() => {
        touch.current = null;
        setDrag(0);
        setPaused(false);
      }}
    >
      {item.logo ? <div className="hero__backdrop" style={{ backgroundImage: `url("${imageUrl(item.logo) ?? ''}")` }} /> : null}
      <div className="hero__content" style={drag ? { transform: `translateX(${drag}px)`, opacity: Math.max(0.4, 1 - Math.abs(drag) / 400) } : undefined}>
        <div className="hero__poster">
          <LazyImage key={item.key} className="poster__image" src={imageUrl(item.logo)} label={item.name} />
        </div>
        <div className="hero__text">
          <p className="hero__label">{t('browse.recent')}</p>
          <h2 className="hero__title">{item.name}</h2>
          {facts ? <p className="hero__facts">{facts}</p> : null}
          <button type="button" className="button primary hero__play" onClick={() => onOpen(item)}>
            <Icon name="play" /> {item.url === null ? t('browse.episodes') : t('player.play')}
          </button>
        </div>
      </div>
      {count > 1 ? (
        <>
          <button type="button" className="hero__arrow hero__arrow--prev" tabIndex={-1} aria-label={t('browse.previous')} onClick={() => go(-1)}>
            <Icon name="back" />
          </button>
          <button type="button" className="hero__arrow hero__arrow--next" tabIndex={-1} aria-label={t('browse.next')} onClick={() => go(1)}>
            <Icon name="next" />
          </button>
          <div className="hero__dots">
            {items.map((entry, position) => (
              <button key={entry.key} type="button" className="slider__dot" tabIndex={-1} aria-label={entry.name} aria-current={position === current} onClick={() => setIndex(position)} />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
